import React, { useState, useEffect } from 'react';
import { Calculator, TrendingDown, RefreshCw } from 'lucide-react';
import { CAMPAIGN_DRIVER_OPTIONS, MAX_CAMPAIGN_DRIVERS } from '../lib/campaign';
import { useCampaignQuote } from '../hooks/useCampaignQuote';

const WRAP_OPTIONS = [
  { key: 'testimonial_partial_wrap', label: 'Testimonial Partial Wrap', sub: '$681/mo per driver · 3 months' },
  { key: 'partial_wrap',             label: 'Partial Wrap', sub: '' },
  { key: 'full_wrap',                label: 'Full Wrap', sub: '' },
];

const PricePreview = () => {
  const [wrap, setWrap] = useState('partial_wrap');
  const [months, setMonths] = useState(5);
  const [drivers, setDrivers] = useState(1);
  const { quote, loading, error, retry } = useCampaignQuote(wrap, months, drivers);

  const isTestimonial = wrap === 'testimonial_partial_wrap';

  // Testimonial campaigns keep the 3-month term but allow the same 1–6 drivers.
  useEffect(() => {
    if (isTestimonial && months !== 3) setMonths(3);
  }, [isTestimonial, months]);

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <section className="bg-cream-light py-14" data-testid="price-preview-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-navy/10 text-navy text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
            <Calculator size={14} /> Transparent pricing
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-navy">See exactly what your campaign will cost.</h2>
          <p className="text-navy/70 mt-2 max-w-2xl mx-auto">
            Live calculator — no sales call required. Adjust the wrap type, length, and driver count to see your real monthly + total.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-navy/5 p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="space-y-5 min-w-0">
            <div>
              <label className="block text-xs uppercase tracking-wider text-navy/60 font-semibold mb-2">Wrap type</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {WRAP_OPTIONS.map(o => (
                  <button
                    key={o.key}
                    onClick={() => setWrap(o.key)}
                    className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${
                      wrap === o.key ? 'border-navy bg-navy/5' : 'border-navy/10 hover:border-navy/30'
                    }`}
                    data-testid={`wrap-option-${o.key}`}
                  >
                    <div className="font-semibold text-navy text-sm">{o.label}</div>
                    {o.sub && <div className="text-xs text-emerald-700 mt-0.5">{o.sub}</div>}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-wider text-navy/60 font-semibold">Campaign length</label>
                <span className="text-sm font-bold text-navy" data-testid="quote-duration-value">{months} months</span>
              </div>
              <input
                type="range"
                min="3"
                max="6"
                value={months}
                onChange={(e) => setMonths(parseInt(e.target.value, 10))}
                disabled={isTestimonial}
                className={`w-full accent-navy ${isTestimonial ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="months-slider"
              />
              <div className="flex justify-between text-xs text-navy/50 mt-1">
                <span>3 mo</span><span>4 mo</span><span>5 mo</span><span>6 mo</span>
              </div>
              {isTestimonial ? (
                <p className="text-xs text-amber-700 mt-1" data-testid="testimonial-lock-note">
                  ✓ Testimonial campaigns are a fixed 3-month promo. Length is locked.
                </p>
              ) : (
                months > 3 && (
                  <p className="text-xs text-emerald-700 mt-1" data-testid="quote-duration-discount-note">
                    ✓ Volume discount unlocked (months beyond 3 reduce the per-driver rate)
                  </p>
                )
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-wider text-navy/60 font-semibold">Drivers</label>
                <span className="text-sm font-bold text-navy" data-testid="quote-driver-count">{drivers} {drivers === 1 ? 'driver' : 'drivers'}</span>
              </div>
              <input
                type="range" min="1" max={MAX_CAMPAIGN_DRIVERS} step="1" value={drivers}
                onChange={(e) => setDrivers(parseInt(e.target.value, 10))}
                className="w-full accent-navy"
                aria-label="Number of campaign drivers"
                data-testid="drivers-slider"
              />
              <div className="flex justify-between text-xs text-navy/50 mt-1">
                {CAMPAIGN_DRIVER_OPTIONS.map(count => <span key={count}>{count}</span>)}
              </div>
              {drivers > 1 && quote && (
                <p className="text-xs text-emerald-700 mt-1" data-testid="quote-multi-driver-discount">
                  ✓ Multi-driver discount: {Math.round((1 - quote.multi_driver_factor) * 100)}% off the total
                </p>
              )}
            </div>
          </div>

          {/* Output */}
          <div className="bg-navy text-cream rounded-2xl p-6 flex flex-col justify-center min-w-0 min-h-[280px]" data-testid="quote-output" aria-live="polite" aria-busy={loading}>
            {quote ? (
              <>
                <div className="text-xs uppercase tracking-widest text-cream/60 font-semibold">Your monthly cost</div>
                <div className="flex flex-wrap items-baseline gap-x-1 font-bold mt-1">
                  <span className="text-3xl sm:text-5xl tabular-nums" data-testid="quote-monthly-cost">{fmt(quote.monthly_cost)}</span>
                  <span className="text-xl text-cream/60">/mo</span>
                </div>
                <div className="text-cream/70 text-sm mt-1" data-testid="quote-total-cost">
                  ≈ {fmt(quote.total_cost)} over {months} {months === 1 ? 'month' : 'months'}
                </div>
                <div className="border-t border-cream/15 my-5"></div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-cream/60 text-xs uppercase tracking-wider">Per-driver / mo</div>
                    <div className="font-bold text-base" data-testid="quote-per-driver-monthly">{fmt(quote.per_driver_monthly)}</div>
                  </div>
                  <div>
                    <div className="text-cream/60 text-xs uppercase tracking-wider">× {drivers} drivers</div>
                    <div className="font-bold text-base" data-testid="quote-gross-monthly">{fmt(quote.gross_monthly)}</div>
                  </div>
                  {quote.multi_driver_factor < 1 && (
                    <div className="col-span-2 flex items-center gap-2 mt-1 text-emerald-300">
                      <TrendingDown size={16} />
                      <span className="text-xs" data-testid="quote-multi-driver-factor">Multi-driver discount × {quote.multi_driver_factor} applied</span>
                    </div>
                  )}
                  {quote.volume_discount_per_month > 0 && (
                    <div className="col-span-2 flex items-center gap-2 text-emerald-300">
                      <TrendingDown size={16} />
                      <span className="text-xs" data-testid="quote-volume-discount">Volume discount: {fmt(quote.volume_discount_per_month)}/mo per driver</span>
                    </div>
                  )}
                </div>
              </>
            ) : error ? (
              <div>
                <p className="text-sm" role="alert" data-testid="quote-error">{error}</p>
                <button type="button" onClick={retry} className="mt-3 inline-flex items-center gap-2 text-sm underline hover:text-white transition-colors" data-testid="quote-retry">
                  <RefreshCw size={14} /> Try again
                </button>
              </div>
            ) : (
              <div className="text-cream/60 text-sm" data-testid="quote-loading">Calculating…</div>
            )}
          </div>
        </div>

        <p className="text-center text-navy/60 text-xs mt-5">
          Pricing covers wrap installation, driver pay-per-mile, hardware, scanning, and dashboard analytics.
          {' '}<a href="/terms" className="underline hover:text-navy" data-testid="quote-pricing-terms-link">See full pricing terms.</a>
        </p>
      </div>
    </section>
  );
};

export default PricePreview;
